<script lang="ts">
  // The flourish after opening a cosmetic box: the rolled item rendered ON the player's character
  // (or a sleeve swatch), a rarity glow, a NEW! / duplicate badge, and the dust a duplicate minted,
  // so a duplicate reads as "progress" (a tradeable copy + dust toward a guaranteed pick), never waste.
  import { scale, fly } from "svelte/transition";
  import { backOut } from "svelte/easing";
  import type { ServerMessage, ChibiLook } from "@ygoevo/world-protocol";
  import { cosmeticName, cosmeticRarity } from "../cosmetics.ts";
  import { GACHA_TEXT, GACHA_BORDER, GACHA_GLOW, GACHA_LABEL } from "../cosmetic-rarity.ts";
  import CosmeticSwatch from "./CosmeticSwatch.svelte";
  import ShardsMeter from "./ShardsMeter.svelte";

  type Result = Extract<ServerMessage, { t: "BoxResult" }>;

  let {
    result,
    look,
    price,
    canAfford,
    busy,
    onAgain,
    onClose,
  }: {
    result: Result;
    look: ChibiLook;
    price: number;
    canAfford: boolean;
    busy: boolean;
    onAgain: () => void;
    onClose: () => void;
  } = $props();

  const rarity = $derived(cosmeticRarity(result.kind, result.itemId));
  const name = $derived(cosmeticName(result.kind, result.itemId));
</script>

<div class="absolute inset-0 z-10 grid place-items-center bg-void/90" role="presentation">
  <div class="flex w-[min(380px,90vw)] flex-col items-center gap-4 p-2" in:scale={{ start: 0.9, opacity: 0, duration: 320, easing: backOut }}>
    <span class="font-display text-xs uppercase tracking-[0.3em] {GACHA_TEXT[rarity]}" in:fly={{ y: -10, duration: 300 }}>
      {GACHA_LABEL[rarity]}
    </span>

    <!-- the item itself: disks/sleeves render solo (centred on their own art), outfits on the
         player's body — CosmeticSwatch picks the right treatment per kind (same as the shop grid),
         so a duel disk shows the DISK, not a cropped headshot. -->
    <div class="relative grid size-64 place-items-center overflow-hidden chamfer border-2 bg-void/50 {GACHA_BORDER[rarity]} {GACHA_GLOW[rarity]}" style="--c:12px">
      <div class="size-56"><CosmeticSwatch kind={result.kind} sku={result.itemId} {look} /></div>
      <span
        class="absolute right-2 top-2 chamfer-btn px-2 py-0.5 font-display text-[0.6rem] font-bold uppercase tracking-widest {result.duplicate ? 'bg-void-2/80 text-ink-dim' : 'bg-cyan text-void'}"
        style="--c:4px"
      >
        {result.duplicate ? `Duplicate ×${result.qty}` : "New!"}
      </span>
    </div>

    <div class="flex flex-col items-center gap-0.5 text-center">
      <span class="font-display text-base font-bold uppercase tracking-wide text-ink">{name}</span>
      {#if result.duplicate}
        <span class="font-display text-[0.66rem] uppercase tracking-wide text-cyan">Tradeable copy · +{result.shardsGained} ◆ shards</span>
      {:else}
        <span class="font-display text-[0.66rem] uppercase tracking-wide text-cyan">Equip it in the Customizer</span>
      {/if}
    </div>

    <div class="w-full chamfer bg-void-2/40 px-3 py-2.5" style="--c:8px">
      <ShardsMeter shards={result.wallet.shards} />
    </div>

    <div class="flex w-full gap-2">
      <button
        onclick={onClose}
        class="flex-1 chamfer-btn bg-void-2/70 py-2 font-display text-xs font-semibold uppercase tracking-[0.14em] text-ink-dim transition-colors hover:text-ink"
        style="--c:7px"
      >
        Done
      </button>
      <button
        onclick={onAgain}
        disabled={busy || !canAfford}
        title={canAfford ? undefined : "Not enough DP"}
        class="flex-1 chamfer-btn py-2 font-display text-xs font-bold uppercase tracking-[0.1em] transition-colors {canAfford ? 'bg-gold/15 text-gold hover:bg-gold/25 hover:glow-cyan' : 'bg-void-2/55 text-magenta/70'} disabled:cursor-not-allowed"
        style="--c:7px"
      >
        {busy ? "Opening…" : `Open Again · ${price} DP`}
      </button>
    </div>
  </div>
</div>
